/**
 * NCB Report page
 *
 * Admin-only. Three actions on the same dataset (Preview / Download /
 * Send-to-NCB), plus a send-history feed. Recipient email is read
 * from System Settings → NCB.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    FileText, Download, Loader2, Calendar, Eye, Send, History, Mail,
    CheckCircle2, XCircle, AlertTriangle,
} from 'lucide-react';
import { Header } from '../../components/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { API_BASE } from '../../lib/apiBase';

const MONTHS = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' },
    { value: '03', label: 'March' },   { value: '04', label: 'April' },
    { value: '05', label: 'May' },     { value: '06', label: 'June' },
    { value: '07', label: 'July' },    { value: '08', label: 'August' },
    { value: '09', label: 'September' },{ value: '10', label: 'October' },
    { value: '11', label: 'November' },{ value: '12', label: 'December' },
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function NcbReportPage() {
    const now = new Date();
    const defaultDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const [year, setYear] = useState(defaultDate.getFullYear());
    const [month, setMonth] = useState(String(defaultDate.getMonth() + 1).padStart(2, '0'));

    const [settings, setSettings] = useState({ recipient_email: '', cc_emails: [] });
    const [preview, setPreview] = useState(null);           // { headers, rows, row_count, window }
    const [history, setHistory] = useState([]);
    const [busy, setBusy] = useState({ preview: false, download: false, send: false });
    const [confirmSend, setConfirmSend] = useState(false);

    const years = useMemo(() => {
        const y = now.getFullYear();
        return [y - 2, y - 1, y, y + 1];
    }, [now]);

    const monthISO = `${year}-${month}`;
    const label = useMemo(() => {
        const m = MONTHS.find((mm) => mm.value === month)?.label || month;
        return `${m} ${year}`;
    }, [month, year]);

    const reloadSettings = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE}/api/reports/ncb/settings`, { headers: authHeaders() });
            setSettings(res.data);
        } catch {
            setSettings({ recipient_email: '', cc_emails: [] });
        }
    }, []);

    const reloadHistory = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE}/api/reports/ncb/history`, { headers: authHeaders() });
            setHistory(res.data || []);
        } catch {
            setHistory([]);
        }
    }, []);

    useEffect(() => { reloadSettings(); reloadHistory(); }, [reloadSettings, reloadHistory]);

    // Preview is per-month; clear it when the picker changes so the user
    // doesn't misread a stale dataset as belonging to the new month.
    useEffect(() => { setPreview(null); }, [year, month]);

    const runPreview = async () => {
        setBusy((b) => ({ ...b, preview: true }));
        try {
            const res = await axios.get(`${API_BASE}/api/reports/ncb/preview`, {
                params: { month: monthISO }, headers: authHeaders(),
            });
            setPreview(res.data);
            if ((res.data?.row_count || 0) === 0) {
                toast.info(`No delivered rows for ${label}`);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to load preview');
        } finally {
            setBusy((b) => ({ ...b, preview: false }));
        }
    };

    const runDownload = async () => {
        setBusy((b) => ({ ...b, download: true }));
        try {
            const res = await axios.get(`${API_BASE}/api/reports/ncb`, {
                params: { month: monthISO }, responseType: 'blob', headers: authHeaders(),
            });
            const blob = new Blob([res.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `NCB_report_${year}${month}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Downloaded NCB report for ${label}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to generate NCB report');
        } finally {
            setBusy((b) => ({ ...b, download: false }));
        }
    };

    const runSend = async () => {
        setConfirmSend(false);
        setBusy((b) => ({ ...b, send: true }));
        try {
            const res = await axios.post(
                `${API_BASE}/api/reports/ncb/send`,
                { month: monthISO },
                { headers: authHeaders() },
            );
            toast.success(`Sent NCB report for ${label} to ${res.data?.recipient || settings.recipient_email}`);
            reloadHistory();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to send NCB report');
            reloadHistory();  // failure is also logged
        } finally {
            setBusy((b) => ({ ...b, send: false }));
        }
    };

    const canSend = !!(settings.recipient_email || '').trim();

    return (
        <div className="min-h-screen bg-background" data-testid="ncb-report-page">
            <Header />
            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                        <FileText className="h-6 w-6 text-slate-600" />
                    </div>
                    <div className="flex-1">
                        <h1 className="text-2xl font-semibold text-slate-900">NCB Report</h1>
                        <p className="text-sm text-slate-500">
                            Monthly export of dispatched orders (Vinyl · CD · DVD · Blu-ray) in NCB template format.
                        </p>
                    </div>
                </div>

                {/* Reporting period + actions */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Calendar className="h-5 w-5 text-slate-500" />
                            Reporting period
                        </CardTitle>
                        <CardDescription>
                            Rows are filtered by the actual dispatch/delivery date recorded on the order.
                            Preview, Download and Send-to-NCB all use the same dataset.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="ncb-year" className="text-xs">Year</Label>
                                <select
                                    id="ncb-year" value={year}
                                    onChange={(e) => setYear(parseInt(e.target.value, 10))}
                                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                                    data-testid="ncb-year"
                                >
                                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                            <div className="space-y-1.5 sm:col-span-3">
                                <Label htmlFor="ncb-month" className="text-xs">Month</Label>
                                <select
                                    id="ncb-month" value={month}
                                    onChange={(e) => setMonth(e.target.value)}
                                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                                    data-testid="ncb-month"
                                >
                                    {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="mt-4 pt-4 border-t flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
                            <div className="text-sm text-slate-600 flex items-center gap-2 flex-wrap">
                                <Mail className="h-4 w-4 text-slate-500" />
                                <span>NCB recipient:</span>
                                {settings.recipient_email ? (
                                    <span className="font-medium text-slate-900" data-testid="ncb-recipient-label">
                                        {settings.recipient_email}
                                    </span>
                                ) : (
                                    <span className="italic text-amber-600 flex items-center gap-1" data-testid="ncb-recipient-missing">
                                        <AlertTriangle className="h-3.5 w-3.5" />
                                        Not configured — set it in System Settings → NCB
                                    </span>
                                )}
                                {settings.cc_emails?.length ? (
                                    <span className="text-xs text-slate-500">
                                        · CC: {settings.cc_emails.join(', ')}
                                    </span>
                                ) : null}
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <Button variant="outline" onClick={runPreview} disabled={busy.preview}
                                        data-testid="ncb-preview-btn">
                                    {busy.preview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                                    Preview
                                </Button>
                                <Button variant="outline" onClick={runDownload} disabled={busy.download}
                                        data-testid="ncb-download-btn">
                                    {busy.download ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                                    Download .xlsx
                                </Button>
                                <Button onClick={() => setConfirmSend(true)} disabled={busy.send || !canSend}
                                        data-testid="ncb-send-btn">
                                    {busy.send ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                                    Send to NCB
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Preview table */}
                {preview && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                Preview — {label}
                                <Badge variant="outline">{preview.row_count} row{preview.row_count === 1 ? '' : 's'}</Badge>
                            </CardTitle>
                            <CardDescription>
                                Window: {preview.window?.label}. Same dataset that will be downloaded / emailed.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {preview.row_count === 0 ? (
                                <p className="text-sm text-slate-500 italic">No delivered rows in this month.</p>
                            ) : (
                                <div className="overflow-x-auto border rounded-md" data-testid="ncb-preview-table">
                                    <table className="w-full text-xs">
                                        <thead className="bg-slate-50">
                                            <tr>
                                                {preview.headers.map((h, i) => (
                                                    <th key={i} className="text-left px-2 py-1.5 font-medium text-slate-700 border-b">
                                                        {h}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {preview.rows.map((row, ri) => (
                                                <tr key={ri} className="hover:bg-slate-50">
                                                    {row.map((cell, ci) => (
                                                        <td key={ci} className="px-2 py-1.5 border-b border-slate-100 align-top whitespace-nowrap">
                                                            {cell === '' || cell === null || cell === undefined ? (
                                                                <span className="text-slate-300">—</span>
                                                            ) : String(cell)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Send history */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <History className="h-5 w-5 text-slate-500" />
                            Send history
                        </CardTitle>
                        <CardDescription>Last 50 NCB send attempts, newest first.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {history.length === 0 ? (
                            <p className="text-sm text-slate-500 italic">No sends yet.</p>
                        ) : (
                            <div className="overflow-x-auto border rounded-md" data-testid="ncb-history-table">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-medium">Month</th>
                                            <th className="text-left px-3 py-2 font-medium">Sent at</th>
                                            <th className="text-left px-3 py-2 font-medium">Recipient</th>
                                            <th className="text-left px-3 py-2 font-medium">Sent by</th>
                                            <th className="text-left px-3 py-2 font-medium">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map((h) => (
                                            <tr key={h.id} className="hover:bg-slate-50">
                                                <td className="px-3 py-2 border-b border-slate-100">{h.month}</td>
                                                <td className="px-3 py-2 border-b border-slate-100 text-slate-600">
                                                    {new Date(h.sent_at).toLocaleString()}
                                                </td>
                                                <td className="px-3 py-2 border-b border-slate-100">
                                                    {h.recipient}
                                                    {h.cc_emails?.length ? (
                                                        <span className="block text-xs text-slate-400">
                                                            CC: {h.cc_emails.join(', ')}
                                                        </span>
                                                    ) : null}
                                                </td>
                                                <td className="px-3 py-2 border-b border-slate-100 text-slate-600">
                                                    {h.sent_by_name || h.sent_by || '—'}
                                                </td>
                                                <td className="px-3 py-2 border-b border-slate-100">
                                                    {h.status === 'sent' ? (
                                                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 inline-flex items-center gap-1">
                                                            <CheckCircle2 className="h-3.5 w-3.5" /> Sent
                                                        </Badge>
                                                    ) : (
                                                        <Badge className="bg-red-100 text-red-700 border-red-200 inline-flex items-center gap-1"
                                                               title={h.error || ''}>
                                                            <XCircle className="h-3.5 w-3.5" /> Failed
                                                        </Badge>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </main>

            <AlertDialog open={confirmSend} onOpenChange={setConfirmSend}>
                <AlertDialogContent data-testid="ncb-send-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Send NCB report</AlertDialogTitle>
                        <AlertDialogDescription>
                            The NCB report for
                            <span className="font-semibold text-slate-800 mx-1">{label}</span>
                            will be generated and emailed as an attachment to:
                            <span className="block mt-2 font-semibold text-slate-800">
                                {settings.recipient_email || '(no recipient configured)'}
                            </span>
                            {settings.cc_emails?.length ? (
                                <span className="block text-sm text-slate-500">
                                    CC: {settings.cc_emails.join(', ')}
                                </span>
                            ) : null}
                            {preview && preview.month === monthISO ? (
                                <span className="block mt-3 text-sm text-slate-500">
                                    {preview.row_count} delivery row{preview.row_count === 1 ? '' : 's'} will be included.
                                </span>
                            ) : null}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="ncb-send-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={runSend} data-testid="ncb-send-confirm">
                            Send now
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
